import { useState } from "react";
import { Popover } from "react-tiny-popover";
import "react-pdf/dist/Page/TextLayer.css";
import { DELETE, POST } from "../../api/requests";
import _ from "lodash";
import ShipmentTag from "../../components/ShipmentTag";
import { ColorRing } from "react-loader-spinner";
import { Building, Loader, Trash, TriangleAlert } from "lucide-react";
import { motion } from "framer-motion";
import DynamicWorkflowForm from "../DynamicWorkflowForm";
import { useDialog } from "../../contexts/DialogContext";
import OverlappingLogos from "../../components/OverlappingLogos";
import toast from "react-hot-toast";
import { useAudio } from "../../contexts/AudioContext";

export default function ApprovalItem({ automation, tab, onDelete }) {
  const [isLoading, setIsLoading] = useState(false);
  
  const { muted, setMuted } = useAudio();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { openDialog, closeDialog } = useDialog();
  const runAutomation = async (id, contextDataOverride) => {
    setIsLoading(true);
    try {
      const response = await POST(`/approvals/${automation.id}/approve`, {
        approvalData: contextDataOverride,
      });
      setIsLoading(false);
      console.log({ response });
      if (response && !response.error) {
        if (!muted) {
          const audio = new Audio("./sound/complete.mp3");
          audio.play();
        }
        toast(
          <div className="flex items-center space-x-2">
            <ColorRing
              visible={true}
              height="20"
              width="20"
              ariaLabel="color-ring-loading"
              wrapperClass="color-ring-wrapper"
              colors={["#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"]}
            />
            <p>Submitting...</p>
          </div>
        );
        closeDialog();
      } else {
        toast.error(response.error || "Server error while submitting request.");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error sending approval signal:", error);
      toast.error("Failed to send approval signal");
      return; 
    }
  };

  const deleteAutomation = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this automation?"
    );
    if (confirmDelete) {
      try {
        await onDelete(id);
      } catch (error) {
        console.error("Error deleting automation:", error);
        // Error handling is now done in the parent component
      }
    }
  };

  const buttonDisabled = false;

  // Get the nodes of the workflow and sort them based on their index
  const nodes = Object.values(
    automation.nodes || automation.workflow?.nodes || {}
  ).sort((a, b) => a.index - b.index);

  // show the run automation button only if the automation is not a scheduled job
  const hideButton = automation.approved;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timeOptions = { hour: "numeric", minute: "numeric", hour12: true };
    const formattedTime = date.toLocaleString("en-US", timeOptions);

    if (isToday) {
      return `Today, ${formattedTime}`;
    } else if (isYesterday) {
      return `Yesterday, ${formattedTime}`;
    } else {
      const monthDay = date.toLocaleString("en-US", {
        month: "numeric",
        day: "numeric",
      });
      return `${monthDay}, ${formattedTime}`;
    }
  };
  console.log({ automation });
  const reviewAutomation = (id) => {
    openDialog(
      <DynamicWorkflowForm
        contextData={automation.descopedContextObject}
        automationId={automation.id}
        interfaceDefinition={automation.interfaceDefinition}
        currentNode={automation.currentNode}
        sourceDataRenderer={automation?.interfaceDefinition.sourceDataRenderer}
        workflow={automation.workflowInfo}
        onRunAutomation={runAutomation}
        isLoading={isLoading}
      />
    );
  };


  return (
    <div
      id={`activity:${automation.id}`}
      className="w-full hover:bg-slate-50 hover:bg-opacity-80 py-2 border-b pl-4 border-slate-100 bg-white"
    >
      <div className="justify-between flex items-center pr-3 space-x-2 text-sm text-gray-800">
        <div className="flex items-center w-full">
          <img
            src={`./status_icons/${
              // Status of most recent takes precedence over whether or not it was auto-queued
              automation.approved ? "completed" : "pending"
            }.svg`}
            style={{
              width: 16,
              marginRight: 10,
              opacity: 1,
            }}
          />

          <div className="flex items-center space-x-2 " style={{ width: 300 }}>
            <div
              onClick={() => {
                if (automation.currentNode?.requiresReview || automation.interfaceDefinition) {
                  reviewAutomation(automation.id);
                }
              }}
            >
              <ShipmentTag
                shipment={{
                  container:
                    _.get(
                      automation.descopedContextObject?.data,
                      automation.currentNode?.shipmentTag?.reference
                    ) || automation.interfaceDefinition?.references[0] || "No ref no.",
                  shipper:
                    _.get(
                      automation.descopedContextObject?.data,
                      automation.currentNode?.shipmentTag?.shipper
                    )|| automation.interfaceDefinition?.references[1] || "No shipper",
                }}
              />
            </div>
          </div>
          <div
            className="flex items-center space-x-1"
            style={{ minWidth: 320, maxWidth: 480 }}
          >
            <OverlappingLogos
              name={automation.currentNode?.displayTitle || automation.interfaceDefinition?.displayTitle}
              platforms={automation.currentNode?.platforms || automation.interfaceDefinition?.platforms}
            />
            <p
              className="font-medium text-xs text-slate-800 truncate w-full"
              style={{ width: 320 }}
            >
              {automation.currentNode?.displayTitle || automation.interfaceDefinition?.displayTitle}
            </p>
            <p className="text-slate-400 text-right" style={{ fontSize: 10 }}>
            {automation.tenant || "Your team"}
          </p>
          </div>
          <div
            className="flex items-center justify-between  space-x-2 ml-3 "
            style={{ width: 140 }}
          >
            {true == false && (
              <div className="flex items-center space-x-2">
                <Loader color="#334155" strokeWidth={2.5} size={12} />
                <p
                  className="flex text-xs font-medium text-slate-700 truncate text-left"
                  style={{ fontSize: 10, minWidth: 0 }}
                >
                  {automation.workflow.metadata.source || "Rectangle"}
                </p>
              </div>
            )}
            <p className="text-slate-400 text-right" style={{ fontSize: 10 }}>
              {formatDate(automation.action_time || automation.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-2 ml-3" style={{ width: 200 }}>
          
          
        </div>
        <div className="flex items-center w-fit py-3 relative justify-end">
          {tab !== "Review" && <div className="h-7 py-2"></div>}
          {tab === "Review" && (
            <div className="flex space-x-2 items-center">
              {automation.workflowInfo.attempt > 1 && (
                <Popover
                  isOpen={isPopoverOpen}
                  positions={["top", "bottom", "left", "right"]}
                  content={
                    <motion.div
                      style={{ zIndex: 9999 }}
                      initial={{ opacity: 0, y: 20, scale: 0.8 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="text-white text-xs bg-slate-800 rounded-md py-1.5 px-3"
                    >
                      This workflow has failed. Restart to try again
                    </motion.div>
                  }
                >
                  <div
                    onMouseEnter={() => {
                      setIsPopoverOpen(true);
                    }}
                    onMouseLeave={() => {
                      setIsPopoverOpen(false);
                    }}
                    className="border cursor-help w-fit hover:bg-slate-50 flex items-center space-x-1 font-medium text-slate-700 rounded-full px-3 py-0.5"
                    style={{ fontSize: 10 }}
                  >
                    <TriangleAlert
                      color="#ff4000"
                      strokeWidth={2.5}
                      size={10}
                    />
                    <p>Retry</p>
                  </div>
                </Popover>
              )}
              <button
                onClick={() => deleteAutomation(automation.id)}
                className="bg-slate-100 rounded-md p-2"
              >
                <Trash size={13} />
              </button>

              
              {automation.approved == false && (
                <button
                  disabled={isLoading}
                  onClick={() => {
                    if (automation.currentNode?.requiresReview || automation.interfaceDefinition) {
                      reviewAutomation(automation.id);
                    } else if (automation.currentNode?.requiresApproval) {
                      runAutomation(automation.id);
                    }
                  }}
                  style={{
                    backgroundColor: buttonDisabled ? "#f1f5f9" : "#C2FB20",
                    color: "#0F172A",
                  }}
                  className="py-1.5 flex hover:bg-opacity-70 items-center space-x-1.5 font-medium text-xs px-2.5 rounded-md whitespace-nowrap"
                >
                  {isLoading && (
                    <ColorRing
                      visible={true}
                      height="16"
                      width="16"
                      ariaLabel="blocks-loading"
                      colors={[
                        "#0F172A",
                        "#0F172A",
                        "#0F172A",
                        "#0F172A",
                        "#0F172A",
                      ]}
                    />
                  )}

                  <span>
                    {(automation.currentNode?.requiresReview || automation.interfaceDefinition )
                      ? "Review"
                      : automation.currentNode?.requiresApproval
                      ? "Run"
                      : "Review"}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
